import {
  Paper,
  Stack,
  Group,
  Text,
  Divider,
  LoadingOverlay,
  Badge,
  Button,
  Box,
} from '@mantine/core';
import { IconArrowNarrowRight, IconCheck, IconX } from '@tabler/icons-react';
import { useTranslation } from 'react-i18next';
import { useConsent } from '@hooks/useConsent';
import { AttachmentDisplayContext } from '@medplum-components/AttachmentDisplay/AttachmentDisplayContext';
import { formatHumanName } from '@medplum/core';
import { Consent, HumanName, ServiceRequest } from '@medplum/fhirtypes';
import { getTranslatedRelationshipDisplay } from '@/utils/consent.utils';
import { DateDisplayFormatter, useDateDisplay } from '@hooks/useDateDisplay';
import { useConceptMap } from '../hooks/useConceptMap';
import { DocumentReferenceDisplay } from '@/components/DocumentReferenceDisplay';

interface ConsentDisplayProps {
  serviceRequest: ServiceRequest;
  consent?: Consent;
  onEdit?: () => void;
}

interface ConsentItemProps {
  label: string;
  value: boolean | null;
  date: Date | null;
  accepted: string;
  declined: string;
  formatDate: DateDisplayFormatter;
}

// Define ConsentItem component outside parent scope
const ConsentItem = ({
  label,
  value,
  date,
  accepted,
  declined,
  formatDate,
}: ConsentItemProps) => (
  <Group justify="space-between" p="sm" bg="gray.0">
    <Group>
      {value ? (
        <IconCheck size={20} color="green" />
      ) : (
        <IconX size={20} color="red" />
      )}
      <Text size="sm">{label}</Text>
    </Group>
    <Group>
      {date && (
        <Text size="sm" c="dimmed">
          {formatDate(date)}
        </Text>
      )}
      <Badge color={value ? 'green' : 'red'} variant="light">
        {value ? accepted : declined}
      </Badge>
    </Group>
  </Group>
);

export function ConsentDisplay({
  serviceRequest,
  consent: initialConsent,
  onEdit,
}: ConsentDisplayProps) {
  const { t } = useTranslation(['lims', 'common']);
  const { loading, consent, consentData, documents } = useConsent(
    serviceRequest,
    initialConsent
  );
  const { isConsentRequired } = useConceptMap();
  const formatDate = useDateDisplay();
  const formatDateTime = useDateDisplay(
    {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    },
    ''
  );

  if (loading) {
    return (
      <Paper shadow="sm" p="md" withBorder pos="relative">
        <LoadingOverlay visible />
      </Paper>
    );
  }

  if (!consent || !consentData) {
    return null;
  }

  // Determine if general consent is denied
  const isGeneralConsentDenied = consentData.general === false;
  const isGeneralConsentAccepted = consentData.general === true;

  const signerRelationshipDisplay = (() => {
    if (consentData.signerType === 'patient') {
      return undefined;
    }
    const relationshipDisplay =
      consentData.selectedRelatedPerson?.relationship?.[0]?.coding?.[0]
        ?.display || consentData.newRelatedPersonRelationship;
    if (relationshipDisplay) {
      return ` (${getTranslatedRelationshipDisplay(relationshipDisplay, t)})`;
    }
    return ` (${t('consent.signer.other', { ns: 'lims' })})`;
  })();

  return (
    <Paper shadow="sm" p="md" withBorder>
      <Stack gap="md">
        <Stack gap={0}>
          <Group justify="space-between">
            <Group gap="xs">
              <Text size="sm" fw={600}>
                {t('consent.display.title', { ns: 'lims' })}
              </Text>
              <Badge
                color={isGeneralConsentDenied ? 'red' : 'green'}
                variant="light"
              >
                {isGeneralConsentDenied
                  ? t('consent.display.denied', { ns: 'lims' })
                  : t('consent.display.completed', { ns: 'lims' })}
              </Badge>
            </Group>

            {onEdit && (
              <Button
                variant="white"
                rightSection={<IconArrowNarrowRight />}
                onClick={onEdit}
                ml={-10}
              >
                {t('edit', { ns: 'common' })}
              </Button>
            )}
          </Group>
          <Text size="xs" c="dimmed">
            {'Υπ: '}
            {consentData.signerType === 'patient'
              ? t('consent.signer.patient', { ns: 'lims' })
              : formatHumanName(
                  consentData.selectedRelatedPerson?.name?.[0] as HumanName
                ) ||
                consentData.newRelatedPersonName ||
                t('consent.signer.unknown', { ns: 'lims' })}
            {signerRelationshipDisplay}
          </Text>
        </Stack>

        <Stack gap="sm">
          {/* General Consent - Always shown at the top */}
          <ConsentItem
            label={t('consent.sections.general.title', { ns: 'lims' })}
            value={consentData.general}
            date={consentData.generalDate}
            accepted={t('consent.display.accepted', { ns: 'lims' })}
            declined={t('consent.display.declined', { ns: 'lims' })}
            formatDate={formatDate}
          />

          {/* Other consents - only show if general consent is accepted */}
          {isGeneralConsentAccepted && (
            <Stack gap="sm" ml="xl">
              <ConsentItem
                label={t('consent.sections.scientificUse.title', {
                  ns: 'lims',
                })}
                value={consentData.scientificUse}
                date={consentData.scientificUseDate}
                accepted={t('consent.display.accepted', { ns: 'lims' })}
                declined={t('consent.display.declined', { ns: 'lims' })}
                formatDate={formatDate}
              />

              {consentData.scientificUse &&
                isConsentRequired(serviceRequest, 'photo-video') && (
                  <Box ml="xl">
                    <ConsentItem
                      label={t('consent.sections.photoVideo.checkbox', {
                        ns: 'lims',
                      })}
                      value={consentData.photoVideoUse}
                      date={consentData.photoVideoUseDate || null}
                      accepted={t('consent.display.accepted', { ns: 'lims' })}
                      declined={t('consent.display.declined', { ns: 'lims' })}
                      formatDate={formatDate}
                    />
                  </Box>
                )}

              {isConsentRequired(serviceRequest, 'incidental-findings') && (
                <ConsentItem
                  label={t('consent.sections.incidentalFindings.title', {
                    ns: 'lims',
                  })}
                  value={consentData.incidentalFindings}
                  date={consentData.incidentalFindingsDate}
                  accepted={t('consent.display.accepted', { ns: 'lims' })}
                  declined={t('consent.display.declined', { ns: 'lims' })}
                  formatDate={formatDate}
                />
              )}

              {isConsentRequired(serviceRequest, 'secondary-findings') && (
                <ConsentItem
                  label={t('consent.sections.secondaryFindings.title', {
                    ns: 'lims',
                  })}
                  value={consentData.secondaryFindings}
                  date={consentData.secondaryFindingsDate}
                  accepted={t('consent.display.accepted', { ns: 'lims' })}
                  declined={t('consent.display.declined', { ns: 'lims' })}
                  formatDate={formatDate}
                />
              )}
            </Stack>
          )}
        </Stack>

        {documents.length > 0 && (
          <>
            <Divider />
            <Stack gap="sm">
              <AttachmentDisplayContext.Provider value={{ preview: false }}>
                {documents.map((doc) => (
                  <DocumentReferenceDisplay file={doc} readonly />
                ))}
              </AttachmentDisplayContext.Provider>
            </Stack>
          </>
        )}

        {consent?.meta?.lastUpdated && (
          <Text size="xs" c="dimmed" ta="right">
            {t('consent.display.lastUpdated', { ns: 'lims' })}:{' '}
            {formatDateTime(consent.meta.lastUpdated)}
          </Text>
        )}
      </Stack>
    </Paper>
  );
}
